import { pushWithRegion } from 'actions/NavigationActions'
import { CheckoutPageId } from 'checkout/constants/pages'
import { getCheckoutPathWithCartId } from 'lib/url/pathsUtils'
import { resetFlightsTimer } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import { getSelectedTourFlightItems, getSelectedTourFlightAirportsRequest, getTourFlightSearch, isTourFlightUpsellAvailable, tourFlightNeedsRefresh } from 'checkout/selectors/tourFlightSelectors'
import qs from 'qs'
import { addItem, updateCheckoutItem } from 'actions/CheckoutActions'
import type { AppAction } from 'actions/types/ActionTypes'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { getNonCharterFlightItems } from 'checkout/selectors/view/flights'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import fetchTourFlightAirports from 'actions/tour/fetchTourFlightAirports'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import { CHECKOUT_SET_TOUR_FLIGHT_SEARCH, CHECKOUT_SET_TOUR_FLIGHT_RECOVERY } from 'actions/actionConstants'
import { clearFlightSearchCacheEntry } from 'actions/FlightActions'
import { getFlightSearchKey, getFlightSearchLuxPlusCacheKey } from 'lib/flights/flightUtils'

export function setTourFlightRecovery(returningItemId?: string): AppAction {
  return { type: CHECKOUT_SET_TOUR_FLIGHT_RECOVERY, returningItemId }
}

type TourLegSearch = ReturnType<typeof getTourFlightSearch>['departing' | 'returning']

function copyTourFlightSearch(item: App.Checkout.FlightItem, search: TourLegSearch): App.Checkout.FlightItem {
  const itinerary = search.itinerary[0]
  const searchChanged = item.originAirportCode !== itinerary.departureAirport ||
    item.destinationAirportCode !== itinerary.arrivalAirport ||
    (item.tourFlightDepartureDate ?? item.flights[0]?.departingDate) !== itinerary.departureDate ||
    item.selectedCabinClass !== search.fareClass

  return {
    ...item,
    originAirportCode: itinerary.departureAirport,
    destinationAirportCode: itinerary.arrivalAirport,
    tourFlightDepartureDate: itinerary.departureDate,
    selectedCabinClass: search.fareClass,
    fareType: search.fareType,
    viewType: search.viewType,
    occupants: search.occupancies[0],
    ...(searchChanged && {
      flights: [],
      searchId: undefined,
      expiry: undefined,
      hasFinalFare: false,
      fareFetchError: undefined,
      totalFare: undefined,
      otherFees: undefined,
    }),
  }
}

/** Save local edits first, then synchronise only the legs already in the cart. */
export function setTourFlightSearch(search: App.TourFlightSearchEdits): AppAction {
  return (dispatch, getState) => {
    dispatch({ type: CHECKOUT_SET_TOUR_FLIGHT_SEARCH, search })
    const searches = getTourFlightSearch(getState())
    for (const item of getNonCharterFlightItems(getState())) {
      if (item.tourFlightLeg) {
        dispatch(updateCheckoutItem(copyTourFlightSearch(item, searches[item.tourFlightLeg])))
      }
    }
  }
}

/** Reuse a leg's draft on back navigation; never bundle it with the tour price. */
export function prepareTourFlightLeg(leg: 'departing' | 'returning'): AppAction {
  return (dispatch, getState) => {
    if (!isTourFlightUpsellAvailable(getState())) return

    const tourItem = getTourV2Items(getState())[0]
    if (tourItem?.purchasableOption.fkDepartureId) {
      dispatch(fetchTourFlightAirports(tourItem.offerId, tourItem.purchasableOption.fkDepartureId))
    }
    const tourAirports = getSelectedTourFlightAirportsRequest(getState())?.data
    if (!tourAirports?.arrivalAirportCode || !tourAirports.departureAirportCode) return

    const search = getTourFlightSearch(getState())[leg]
    const itinerary = search.itinerary[0]
    if (!itinerary.departureAirport || !itinerary.arrivalAirport || !itinerary.departureDate) return
    let item = getNonCharterFlightItems(getState()).find(item => item.tourFlightLeg === leg)
    if (!item) {
      item = {
        ...generateFlightCheckoutItem({
          originAirportCode: itinerary.departureAirport,
          destinationAirportCode: itinerary.arrivalAirport,
          occupants: search.occupancies[0],
          passengers: generatePassengersFromOccupants(search.occupancies[0]),
          fareType: search.fareType,
          viewType: search.viewType,
          selectedCabinClass: search.fareClass,
        }),
        tourFlightLeg: leg,
        tourFlightDepartureDate: itinerary.departureDate,
      }
      dispatch(addItem(item))
    } else {
      item = copyTourFlightSearch(item, search)
      dispatch(updateCheckoutItem(item))
    }
    return qs.stringify({ flightItemId: item.itemId })
  }
}

/** Clear expired selected legs and restart at the first one in checkout order. */
export function refreshTourFlightSearch(leg?: 'departing' | 'returning'): AppAction {
  return (dispatch, getState) => {
    if (getState().checkout.tourFlightRecoveryReturningItemId) dispatch(setTourFlightRecovery())
    const state = getState()
    if (!isTourFlightUpsellAvailable(state)) return
    const tourAirports = getSelectedTourFlightAirportsRequest(state)?.data
    if (!tourAirports?.arrivalAirportCode || !tourAirports.departureAirportCode) {
      dispatch(prepareTourFlightLeg(leg ?? 'departing'))
      return
    }
    const selected = getSelectedTourFlightItems(state)
    const expired = selected.filter(item => tourFlightNeedsRefresh(item, state))
    const flights = getNonCharterFlightItems(state).filter(item => item.tourFlightLeg)
    const item = expired[0] ?? selected.find(item => item.tourFlightLeg === leg) ?? selected[0] ?? flights.find(item => item.tourFlightLeg === leg) ?? flights[0]
    if (!item?.tourFlightLeg) return

    const searches = getTourFlightSearch(state)
    for (const flight of expired.length ? expired : [item]) {
      if (flight.tourFlightLeg) {
        const search = searches[flight.tourFlightLeg]
        // Refresh both pricing variants without changing the selector-derived request key.
        for (const requestLuxPlusPricing of [false, true]) {
          dispatch(clearFlightSearchCacheEntry(getFlightSearchKey({
            ...search,
            currency: state.geo.currentCurrency,
            key: getFlightSearchLuxPlusCacheKey(search.key, requestLuxPlusPricing),
          })))
        }
      }
      dispatch(updateCheckoutItem({
        ...flight,
        flights: [],
        searchId: undefined,
        expiry: undefined,
        hasFinalFare: false,
        fareFetchError: undefined,
      }))
    }
    const search = dispatch(prepareTourFlightLeg(item.tourFlightLeg))
    const page = item.tourFlightLeg === 'departing' ? CheckoutPageId.DepartingFlights : CheckoutPageId.ReturningFlights
    resetFlightsTimer()
    dispatch(pushWithRegion(getCheckoutPathWithCartId(page, state.checkout.cart.cartId), search))
  }
}

/** Restart unavailable tour flights while preserving a leg identified as unaffected. */
export function recoverUnavailableTourFlights(failedItemId?: string): AppAction {
  return (dispatch, getState) => {
    dispatch(setTourFlightRecovery())
    const state = getState()
    const flights = getNonCharterFlightItems(state).filter(item => item.tourFlightLeg)
    const failed = flights.find(item => item.itemId === failedItemId)
    const leg = failed?.tourFlightLeg ?? 'departing'
    const searches = getTourFlightSearch(state)
    for (const item of failed ? [failed] : flights) {
      if (!item.tourFlightLeg) continue
      const search = searches[item.tourFlightLeg]
      for (const requestLuxPlusPricing of [false, true]) {
        dispatch(clearFlightSearchCacheEntry(getFlightSearchKey({
          ...search,
          currency: state.geo.currentCurrency,
          key: getFlightSearchLuxPlusCacheKey(search.key, requestLuxPlusPricing),
        })))
      }
      dispatch(updateCheckoutItem({
        ...item,
        flights: [],
        searchId: undefined,
        expiry: undefined,
        hasFinalFare: false,
        fareFetchError: undefined,
        totalFare: undefined,
        otherFees: undefined,
      }))
    }
    const search = dispatch(prepareTourFlightLeg(leg))
    dispatch(setTourFlightRecovery(failed?.tourFlightLeg === 'departing' ? flights.find(item => item.tourFlightLeg === 'returning')?.itemId : undefined))
    const page = leg === 'returning' ? CheckoutPageId.ReturningFlights : CheckoutPageId.DepartingFlights
    resetFlightsTimer()
    dispatch(pushWithRegion(getCheckoutPathWithCartId(page, state.checkout.cart.cartId), search))
  }
}
